import { useQuery } from '@tanstack/react-query';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : (undefined as any);
}

export const api = {
  logout: () => req('/api/users/logout', { method: 'POST' }),
  me: () => req<{ authenticated: boolean }>('/api/me'),

  // Email + password user accounts.
  userSignup: (body: {
    email: string;
    password?: string;
    name?: string;
    workspace_name?: string;
    company_name?: string;
    company_website?: string;
  }) =>
    req<UserOut>('/api/users/signup', { method: 'POST', body: JSON.stringify(body) }),
  requestMagicLink: (email: string) =>
    req<{ ok: boolean }>('/api/auth/magic/request', { method: 'POST', body: JSON.stringify({ email }) }),
  requestAdminMagicLink: (email: string) =>
    req<{ ok: boolean }>('/api/auth/magic/admin/request', { method: 'POST', body: JSON.stringify({ email }) }),
  adminResendMagicLink: (email: string) =>
    req<{ ok: boolean }>('/api/auth/magic/admin/resend', { method: 'POST', body: JSON.stringify({ email }) }),
  billingMe: () => req<BillingMe>('/api/billing/me'),
  listAudit: (params?: { action?: string; tenant_id?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.action) qs.set('action', params.action);
    if (params?.tenant_id) qs.set('tenant_id', params.tenant_id);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return req<AuditRow[]>(`/api/audit-log${q ? '?' + q : ''}`);
  },
  userLogin: (body: { email: string; password: string }) =>
    req<UserOut>('/api/users/login', { method: 'POST', body: JSON.stringify(body) }),
  userMe: () => req<UserMeOut>('/api/users/me'),

  // ── Clients ──
  listClients: (includeArchived = false) =>
    req<Client[]>(`/api/clients${includeArchived ? '?include_archived=true' : ''}`),
  getClient: (idOrSlug: string) => req<Client>(`/api/clients/${idOrSlug}`),
  createClient: (body: ClientCreate) => req<Client>('/api/clients', { method: 'POST', body: JSON.stringify(body) }),
  patchClient: (idOrSlug: string, body: ClientPatch) =>
    req<Client>(`/api/clients/${idOrSlug}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteClient: (idOrSlug: string) => req<OkOut>(`/api/clients/${idOrSlug}`, { method: 'DELETE' }),
  setTenantClient: (id: string, body: { client_id?: string | null; client_slug?: string | null }) =>
    req<Tenant>(`/api/tenants/${id}/client`, { method: 'PATCH', body: JSON.stringify(body) }),
  setClientTenants: (clientId: string, tenantIds: string[]) =>
    req<OkOut>(`/api/clients/${clientId}/tenants`, {
      method: 'PUT', body: JSON.stringify({ tenant_ids: tenantIds }),
    }),

  // ── API tokens ──
  listTokens: (includeRevoked = false) =>
    req<ApiTokenOut[]>(`/api/tokens${includeRevoked ? '?include_revoked=true' : ''}`),
  createToken: (body: ApiTokenCreateIn) =>
    req<ApiTokenCreated>('/api/tokens', { method: 'POST', body: JSON.stringify(body) }),
  revokeToken: (id: string) => req<OkOut>(`/api/tokens/${id}`, { method: 'DELETE' }),

  // ── Per-client sequencer keys ──
  listSequencerCatalog: () => req<SequencerCatalogEntry[]>('/api/sequencers'),
  listSequencerKeys: (clientId: string) =>
    req<SequencerKey[]>(`/api/clients/${clientId}/sequencers`),
  // Legacy single-key upsert: rotates the workspace default for `sequencer`.
  putSequencerKey: (clientId: string, sequencer: string, body: { api_key: string; label?: string }) =>
    req<SequencerKey>(`/api/clients/${clientId}/sequencers/${sequencer}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  deleteSequencerKey: (clientId: string, sequencer: string) =>
    req<OkOut>(`/api/clients/${clientId}/sequencers/${sequencer}`, { method: 'DELETE' }),
  // Multi-key endpoints — preferred when a workspace has more than one
  // account on the same sequencer.
  createSequencerKey: (
    clientId: string,
    body: { sequencer: string; api_key: string; label?: string; is_default?: boolean; oauth_url?: string },
  ) =>
    req<SequencerKey>(`/api/clients/${clientId}/sequencer-keys`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  patchSequencerKey: (clientId: string, keyId: string, body: { api_key?: string; label?: string; is_default?: boolean }) =>
    req<SequencerKey>(`/api/clients/${clientId}/sequencer-keys/${keyId}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  deleteSequencerKeyById: (clientId: string, keyId: string) =>
    req<OkOut>(`/api/clients/${clientId}/sequencer-keys/${keyId}`, { method: 'DELETE' }),

  // ── Tenants ──
  listTenants: (params?: { client_slug?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.client_slug) qs.set('client_slug', params.client_slug);
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return req<Tenant[]>(`/api/tenants${q ? '?' + q : ''}`);
  },
  getTenant: (id: string) => req<TenantDetail>(`/api/tenants/${id}`),
  revealTenantCredentials: (id: string) =>
    req<{
      ga_upn: string;
      ga_password: string | null;
      initial_password: string | null;
      totp_code: string | null;
      totp_seconds_remaining: number | null;
    }>(
      `/api/tenants/${id}/credentials`,
    ),
  updateTenantCredentials: (id: string, body: { ga_password: string; ga_upn?: string | null }) =>
    req<{ ok?: boolean; detail?: any }>(
      `/api/tenants/${id}/credentials`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  createTenant: (body: TenantCreate) => req<Tenant>('/api/tenants', { method: 'POST', body: JSON.stringify(body) }),
  createTenantsBulk: (rows: TenantBulkRow[]) =>
    req<Tenant[]>('/api/tenants/bulk', { method: 'POST', body: JSON.stringify({ rows }) }),
  exportTenantAdminCredentialsCsv: async (tenantIds: string[]): Promise<Blob> => {
    const res = await fetch('/api/tenants/admin-export.csv', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_ids: tenantIds }),
    });
    if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
    return res.blob();
  },
  exportMailboxesCsv: async (tenantIds: string[]): Promise<Blob> => {
    const res = await fetch('/api/tenants/export.csv', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_ids: tenantIds }),
    });
    if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
    return res.blob();
  },
  deleteTenant: (id: string) => req<OkOut>(`/api/tenants/${id}`, { method: 'DELETE' }),
  teardownTenant: (id: string) => req<OkOut>(`/api/tenants/${id}/teardown`, { method: 'POST' }),
  stopTenantRun: (id: string) => req<OkOut>(`/api/tenants/${id}/stop`, { method: 'POST' }),

  createCfZone: (id: string) =>
    req<CfZoneOut>(`/api/tenants/${id}/cf-zone`, { method: 'POST' }),
  checkCfZone: (id: string) =>
    req<CfZoneOut>(`/api/tenants/${id}/cf-zone/check`, { method: 'POST' }),
  updateCredentials: (id: string, body: { ga_password: string; ga_upn?: string }) =>
    req<Tenant>(`/api/tenants/${id}/credentials`, { method: 'POST', body: JSON.stringify(body) }),
  renameTenantMailboxes: (id: string, body: { name_seed: string | null }) =>
    req<OkOut>(`/api/tenants/${id}/rename`, { method: 'POST', body: JSON.stringify(body) }),

  runPhase: (id: string, phase: string) =>
    req<OkOut>(`/api/tenants/${id}/phases/${phase}/run`, { method: 'POST' }),
  runPipeline: (id: string) =>
    req<OkOut>(`/api/tenants/${id}/pipeline/run`, { method: 'POST' }),

  listMailboxes: (id: string) => req<Mailbox[]>(`/api/tenants/${id}/mailboxes`),
  resetFailedPasswords: (id: string) =>
    req<OkOut>(`/api/tenants/${id}/mailboxes/reset-failed-passwords`, { method: 'POST' }),
  reoauthFailed: (id: string) =>
    req<OkOut>(`/api/tenants/${id}/mailboxes/reoauth-failed`, { method: 'POST' }),
  resetOnePassword: (id: string, email: string) =>
    req<OkOut>(`/api/tenants/${id}/mailboxes/${encodeURIComponent(email)}/reset-password`, { method: 'POST' }),
  reoauthOne: (id: string, email: string) =>
    req<OkOut>(`/api/tenants/${id}/mailboxes/${encodeURIComponent(email)}/reoauth`, { method: 'POST' }),

  domainSwap: (id: string, body: DomainSwapIn) =>
    req<DomainSwapOut>(`/api/tenants/${id}/domain-swap`, { method: 'POST', body: JSON.stringify(body) }),

  logBacklog: (id: string) => req<LogLine[]>(`/api/tenants/${id}/logs/backlog`),

  reconnectScan: () => req<ReconnectScanOut>('/api/reconnect/scan'),
  reconnectRun: (emails: string[]) =>
    req<OkOut>('/api/reconnect/run', { method: 'POST', body: JSON.stringify({ emails }) }),
  reconnectJobs: () => req<{ jobs: ReconnectJob[] }>('/api/reconnect/jobs'),
  reconnectState: (jobId: string) => req<ReconnectStateOut>(`/api/reconnect/state/${encodeURIComponent(jobId)}`),
};

// ── Types ─────────────────────────────────────────────────────────
export type Tenant = {
  id: string;
  client_id: string | null;
  client_slug: string | null;
  display_name: string;
  ga_upn: string;
  target_domain: string;
  azure_tenant_id: string | null;
  status: 'queued' | 'running' | 'done' | 'failed' | 'paused' | 'warning' | 'rerun_queued';
  current_phase: string | null;
  name_seed: string | null;
  sequencer: string;
  mailbox_count: number;
  mailbox_total: number;
  connected_count: number;
  error_count: number;
  ns_status: 'not_started' | 'pending' | 'active' | 'timeout' | 'failed' | string;
  cf_nameservers: string[] | null;
  signin_failure: SigninFailure | null;
  pending_swap: PendingSwap | null;
  created_at: string;
  updated_at: string;
};

export type SigninFailure = {
  reason: 'invalid_username' | 'invalid_password' | string;
  message: string;
  at: string;
};

export type PendingSwap = {
  from_domain: string;
  to_domain: string;
  cf_zone_id: string;
  cf_nameservers: string[];
  ns_status: 'pending' | 'active' | 'timeout' | 'failed' | string;
  requested_at: string;
  attempts?: number;
  polled_at?: string;
  first_seen_at?: string;
};

export type CfZoneOut = {
  zone_id: string;
  nameservers: string[];
  ns_status: 'not_started' | 'pending' | 'active' | 'timeout' | 'failed' | string;
};

export type Client = {
  id: string;
  name: string;
  slug: string;
  contact_email: string | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  tenant_count: number;
};
export type ClientCreate = {
  name: string;
  slug?: string;
  contact_email?: string;
  notes?: string;
};
export type ClientPatch = Partial<ClientCreate> & { archived?: boolean };

export type ApiTokenOut = {
  id: string;
  name: string;
  client_id: string | null;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};
export type ApiTokenCreated = ApiTokenOut & { token: string };
export type ApiTokenCreateIn = { name: string; client_id?: string | null; scopes: string[] };

export type UserOut = {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'operator' | 'user' | string;
  client_id: string | null;
  email_verified: boolean;
  created_at: string;
  last_login_at: string | null;
};

export type BillingMe = {
  workspace_id: string | null;
  workspace_name: string | null;
  plan: string;
  subscription_status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'none' | string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  has_stripe_customer: boolean;
  tenants_used: number;
  tenant_cap: number | null;
  mailboxes_used: number;
  mailbox_cap: number | null;
};

export type AuditRow = {
  id: number;
  ts: string;
  actor: string;
  action: string;
  tenant_id: string | null;
  detail: Record<string, unknown>;
};
export type UserMeOut =
  | UserOut
  | { authenticated: boolean; is_admin_session: boolean; is_api_token: boolean; is_user_session: boolean };

export type SequencerCatalogEntry = {
  slug: string;
  name: string;
  status: 'live' | 'planned' | string;
  description: string;
};
export type SequencerKey = {
  id: string;
  client_id: string;
  sequencer: string;
  label: string | null;
  last4: string | null;
  is_default: boolean;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export type PhaseRun = {
  phase: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  attempt: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
};

export type TenantDetail = Tenant & {
  phases: PhaseRun[];
  mailbox_counts: Record<string, number>;
};

export type Mailbox = {
  email: string;
  // Public surface — present for everyone.
  status?: 'pending' | 'provisioning' | 'connected' | 'failed' | string | null;
  error_message?: string | null;
  // Admin-only fields. Operators receive null/undefined for these.
  alias?: string | null;
  upn?: string | null;
  display_name?: string | null;
  created?: boolean | null;
  licensed?: boolean | null;
  perms_granted?: boolean | null;
  password_set?: boolean | null;
  upn_flipped?: boolean | null;
  oauth_done?: boolean | null;
  oauth_error?: string | null;
};

export type TenantCreate = {
  display_name: string;
  ga_upn: string;
  ga_password: string;
  initial_password?: string;
  target_domain: string;
  name_seed?: string;
  mailbox_count?: number;
  client_id?: string | null;
  client_slug?: string | null;
  sequencer?: string;
  sequencer_key_id?: string | null;
  sequencer_key_label?: string | null;
};
export type TenantBulkRow = Omit<TenantCreate, 'display_name'> & {
  display_name?: string;
  client_slug?: string;
};
export type DomainSwapIn = {
  to_domain: string;
  from_domain?: string;
  name_seed?: string | null;
};
export type DomainSwapOut = {
  zone_id: string;
  nameservers: string[];
  ns_status: 'pending' | 'active' | 'timeout' | 'failed' | string;
  job_id: string | null;
};
export type LogLine = { ts: string; phase: string | null; level: string; message: string };
export type OkOut = { ok: boolean; detail?: any };

export type ReconnectRow = {
  email: string;
  mailbox_id: string;
  tenant_id: string;
  target_domain: string;
  ga_upn: string;
  status: number | null;
  label: 'disconnected' | 'reconnecting' | 'paused' | 'pending' | string;
  severity: 'error' | 'warn' | 'ok';
  autofix_failed: boolean;
  status_message: string | null;
  warmup_status: number | null;
  provider_code: number | null;
  timestamp_updated: string | null;
};
export type ReconnectScanOut = {
  items: ReconnectRow[];
  stats: Record<string, number>;
  total_scanned: number;
  total_in_db: number;
};
export type ReconnectJob = {
  id: string;
  status: string;
  total: number;
  ok: number | null;
  failed: number | null;
  started_at: string | null;
  finished_at: string | null;
  enqueued_at: string | null;
};
export type ReconnectStateRow = {
  email: string;
  status: 'queued' | 'running' | 'done' | 'failed' | string;
  step?: string;
  attempt?: number;
  attempts?: number;
  ok?: boolean;
  error?: string | null;
  permanent?: boolean;
  duration_s?: number;
  tenant_id?: string | null;
  target_domain?: string | null;
  started_at?: string;
  finished_at?: string;
};
export type ReconnectMeta = {
  status?: string;
  total?: number;
  ok?: number;
  failed?: number;
  rc?: number;
  started_at?: string;
  finished_at?: string;
  error?: string;
  emails?: string[];
  tenants?: string[];
};
export type ReconnectStateOut = {
  meta: ReconnectMeta;
  rows: ReconnectStateRow[];
};

// ── Hooks ─────────────────────────────────────────────────────────
export const useMe = () => useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
export const useTenants = (params?: { client_slug?: string; status?: string }) =>
  useQuery({
    queryKey: ['tenants', params?.client_slug || null, params?.status || null],
    queryFn: () => api.listTenants(params),
    refetchInterval: 5000,
  });
export const useClients = () =>
  useQuery({ queryKey: ['clients'], queryFn: () => api.listClients(false), refetchInterval: 30000 });
export const useTokens = () =>
  useQuery({ queryKey: ['tokens'], queryFn: () => api.listTokens(false), refetchInterval: 30000 });
export const useTenant = (id: string) =>
  useQuery({ queryKey: ['tenant', id], queryFn: () => api.getTenant(id), refetchInterval: 3000, enabled: !!id });
export const useMailboxes = (id: string) =>
  useQuery({ queryKey: ['mailboxes', id], queryFn: () => api.listMailboxes(id), refetchInterval: 5000, enabled: !!id });
export const useReconnectJobs = () =>
  useQuery({ queryKey: ['reconnect-jobs'], queryFn: api.reconnectJobs, refetchInterval: 3000 });
